import type {
  ChannelLineup,
  ChannelLineupResponse,
  ChannelSummary,
  OnairChannelLineup,
  OnairLineupResponse,
} from '@apm/contracts';
import { config } from './config.js';

/**
 * 채널 편성표 (`GET /v1/channels/lineup`, v25).
 *
 * **`fetchChannels` 와 같은 방침이다 — 던지지 않는다.** 실패해도 화면은 채널 목록만으로
 * 그려지고 카드 자리에 자리표시가 뜬다. **편성표가 실패해도 채널 목록은 그대로 뜨는
 * 것**이 D10 을 지키는 마지막 겹이다 (`docs/51` §2.4).
 *
 * ── ★ v27/TG15 — 실패는 `null` 이고 「빈 편성표」가 아니다 ──────
 *
 * 전에는 실패도 빈 배열이었다. **한 번만 받을 때는 둘이 같은 뜻이었지만**(들고 있던 것이
 * 없다) **다시 받는 자리가 생기면서 갈린다** — 편성표를 여는 순간 네트워크가 한 번 튀면
 * 들고 있던 다섯 행이 통째로 **자리표시로 바뀐다.** 그 실패는 조용하고, 시청자에게는
 * 「편성표를 열었더니 비었다」로 보인다.
 *
 * 그래서 여기서 갈라 준다 — `null` 은 **「덮어쓰지 말라」**이고, `[]` 는 「서버가 정말
 * 비었다고 했다」다.
 *
 * **토큰이 있으면 싣는다** — 없으면 서버가 익명으로 통과시키고 편성표대로의 순서를 준다
 * (절대 규칙 46). `Authorization` 이 붙으므로 단순 요청이 아니고, 프리플라이트는
 * `enableCors` 의 `maxAge` 가 이미 덮는다.
 */
export async function fetchLineup(options: {
  deviceId?: string;
  token?: string | null;
  signal?: AbortSignal;
}): Promise<ChannelLineup[] | null> {
  const base = config.serverUrl.replace(/\/+$/, '');
  const { deviceId, token, signal } = options;
  const query =
    deviceId !== undefined && deviceId !== '' ? `?deviceId=${encodeURIComponent(deviceId)}` : '';
  try {
    const response = await fetch(`${base}/v1/channels/lineup${query}`, {
      ...(signal ? { signal } : {}),
      ...(token != null && token !== ''
        ? { headers: { Authorization: `Bearer ${token}` } }
        : {}),
    });
    if (!response.ok) return null;
    return ((await response.json()) as ChannelLineupResponse).items;
  } catch {
    // 중단(`AbortSignal`)도 여기로 온다 — 그때도 들고 있던 것을 지키는 것이 옳다.
    return null;
  }
}

/**
 * ONAIR 편성표 (`GET {onairBaseUrl}/v1/lineup`, v28 · 절대 규칙 75).
 *
 * ── ★ 앱이 ONAIR 서버를 **직접** 부른다 ─────────────────────
 *
 * BLOOM Server 가 중계하면 편해 보이지만, 그 순간 **BLOOM 이 「몇 시에 무엇」을 말하게
 * 되어** 규칙 73 ①이 뒷문으로 사라진다. 화면은 어느 쪽이든 똑같이 그려진다 — **갈리는
 * 것은 누가 말했는가 하나**다(`docs/57` D7).
 *
 * ── 서버별로 묶어 한 번씩 부른다 ────────────────────────────
 *
 * 같은 ONAIR 서버에 채널이 여럿이다(데모는 둘). 채널마다 부르면 요청이 채널 수만큼 늘고,
 * **편성표를 여는 순간마다** 그렇게 된다(규칙 73 ★).
 *
 * ── 실패는 `null` 이 아니라 **빈 맵**이다 ────────────────────
 *
 * `fetchLineup` 과 갈리는 자리다. 저쪽의 `null` 은 「들고 있던 것을 덮어쓰지 말라」인데,
 * 이쪽은 **행마다 따로** 짝지어지므로 못 받은 채널만 자리표시가 되면 된다 — 그리고
 * **ONAIR 서버가 죽어도 개인화 행과 채널 목록은 그대로 뜬다.**
 */
export async function fetchOnairLineups(
  channels: readonly ChannelSummary[],
  signal?: AbortSignal,
): Promise<Map<string, OnairChannelLineup>> {
  const byUrl = new Map<string, string[]>();
  for (const channel of channels) {
    if (channel.onair === null) continue;
    const ids = byUrl.get(channel.onair.lineupUrl) ?? [];
    ids.push(channel.channelId);
    byUrl.set(channel.onair.lineupUrl, ids);
  }

  const out = new Map<string, OnairChannelLineup>();
  await Promise.all(
    [...byUrl].map(async ([url, ids]) => {
      try {
        const response = await fetch(`${url}?channelIds=${ids.join(',')}`, {
          ...(signal ? { signal } : {}),
        });
        if (!response.ok) return;
        for (const item of ((await response.json()) as OnairLineupResponse).items) {
          out.set(item.channelId, item);
        }
      } catch {
        // 중단도 여기로 온다. 못 받은 채널만 자리표시가 된다.
      }
    }),
  );
  return out;
}
